import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../models"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { toast } from "react-toastify"
import { IAssurancesListingContextPropsModel, IGetAssurancesRequestModel, IGetAssurancesResponseItemModel, IGetAssurancesResponseModel, IItemInPreviewModel } from "./core/_models"
import { getAssurancesListingRequest } from "./core/_requests"
import AssurancesListingFilters from "./AssurancesListingFilters"
import ViviersListingSearchBar from "./AssurancesListingSearchBar"
import AssurancesListingList from "./AssurancesListingList"
import AssurancesListingPreview from "./AssurancesListingPreview"
import useAssuranceListing from "./core/useAssuranceListing"

const REACT_QUERY_KEY_GET_LISTING = "REACT_QUERY_KEY_GET_ASSURANCES_LISTING"
const AssurancesListingContext = createContext<IAssurancesListingContextPropsModel>({} as IAssurancesListingContextPropsModel)
export const useAssurancesListingContext = () => useContext(AssurancesListingContext)
const AssurancesListing = () => {
     const queryClient = useQueryClient()
     const { checkAssurancesAsVerified, checkAssurancesAsNonVerified } = useAssuranceListing()

     // Referencing filters form, simple search bar form & preview container
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)
     const previewContainerRef = useRef<any>(null)

     // Filters, item in preview & bulk selection
     const [filters, setFilters] = useState<IGetAssurancesRequestModel>({
          page: 1,
          pageLength: 50,
          status: "NOT_VERIFIED",
     })
     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [itemInPreview, setItemInPreview] = useState<IItemInPreviewModel | null>(null)
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })
     const [itemsBeingUpdated, setItemsBeingUpdated] = useState<IGetAssurancesResponseItemModel[]>([])

     const listingQuery = useQuery<IGetAssurancesResponseModel, AxiosError>(REACT_QUERY_KEY_GET_LISTING, async () => {
          return getAssurancesListingRequest(filters)
               .then(r => {
                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    return r.data
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors in the filters form
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) filtersFormikRef.current.setFieldError(key, error.errors[key])
                    }

                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    // Toast error
                    toast.error(error?.detail, { autoClose: false })

                    throw e
               })
     })

     useEffect(() => {
          listingQuery.refetch().then()
     }, [filters])

     function handleCheckAssurancesAsVerified(items: IGetAssurancesResponseItemModel[]) {
          checkAssurancesAsVerified(
               items.map(item => item.mission),
               () => setItemsBeingUpdated(items),
               () => handleRefreshItemOfTheList(items).then(() => setItemInPreview(null)),
               () => {
                    setIsBulkSelectionEnabled({
                         enabled: false,
                         checkedElementsInPage: [],
                         areAllElementsInAllPagesChecked: false,
                    })
                    setItemsBeingUpdated([])
               },
               () => {
                    setIsBulkSelectionEnabled({
                         enabled: false,
                         checkedElementsInPage: [],
                         areAllElementsInAllPagesChecked: false,
                    })
                    setItemsBeingUpdated([])
               }
          )
     }

     function handleCheckAssurancesAsNonVerified(items: IGetAssurancesResponseItemModel[]) {
          checkAssurancesAsNonVerified(
               items.map(item => item.mission),
               () => setItemsBeingUpdated(items),
               () => handleRefreshItemOfTheList(items).then(() => setItemInPreview(null)),
               () => {
                    setIsBulkSelectionEnabled({
                         enabled: false,
                         checkedElementsInPage: [],
                         areAllElementsInAllPagesChecked: false,
                    })
                    setItemsBeingUpdated([])
               },
               () => {
                    setIsBulkSelectionEnabled({
                         enabled: false,
                         checkedElementsInPage: [],
                         areAllElementsInAllPagesChecked: false,
                    })
                    setItemsBeingUpdated([])
               }
          )
     }

     // Whenever an item is updated, fresh data will be requested.
     async function handleRefreshItemOfTheList(items: IGetAssurancesResponseItemModel[]) {
          const ids = items.map(item => item.mission.id as number)

          try {
               const { data } = await getAssurancesListingRequest({ ids, page: 1, pageLength: null })
               // If there is a null item in data, refresh the list (this means that the item can't be retrieved)
               const not_returned_ids = ids.filter(id => !data.data.map(item => item.mission.id).includes(id))
               if (not_returned_ids.length > 0) {
                    listingQuery.refetch().then()
               } else {
                    // Otherwise update the listing query data with the given ids
                    queryClient.setQueryData<IGetAssurancesResponseModel | undefined>(REACT_QUERY_KEY_GET_LISTING, (prev: IGetAssurancesResponseModel | undefined) => {
                         if (prev) {
                              prev.data = prev.data.map(item => {
                                   if (ids.includes(item.mission.id as number)) {
                                        const updatedItem = data.data.find(updatedItem => updatedItem.mission.id === item.mission.id)
                                        if (updatedItem) return updatedItem
                                   }
                                   return item
                              })

                              const not_returned_ids = ids.filter(id => !data.data.map(item => item.mission.id).includes(id))
                              prev.data = prev.data.filter(item => !not_returned_ids.includes(item.mission.id as number))
                         }
                         return prev
                    })
               }
          } catch (e) {
               const error = (e as AxiosError).response?.data as IHttpErrorResponseModel
               toast.error(error?.detail, { autoClose: false })
          }
          setItemsBeingUpdated([])
          setIsBulkSelectionEnabled({
               enabled: false,
               areAllElementsInAllPagesChecked: false,
               checkedElementsInPage: [],
          })
     }

     return (
          <>
               <AssurancesListingContext.Provider
                    value={{
                         areFiltersVisible,
                         setAreFiltersVisible,
                         filters,
                         setFilters,
                         itemInPreview,
                         setItemInPreview,
                         REACT_QUERY_KEY_GET_LISTING,
                         listingQuery,
                         filtersFormikRef,
                         previewContainerRef,
                         simpleSearchBarFormikRef,
                         isBulkSelectionEnabled,
                         setIsBulkSelectionEnabled,
                         handleCheckAssurancesAsVerified,
                         handleCheckAssurancesAsNonVerified,
                         itemsBeingUpdated,
                    }}
               >
                    {/* Adapt view to display */}
                    {window.innerWidth >= 1200 ? <ViewXlOrHigher /> : <ViewLgOrLess />}
               </AssurancesListingContext.Provider>
          </>
     )
}

// View display Lg or less
const ViewLgOrLess = () => {
     const context = useAssurancesListingContext()

     return (
          <>
               <div className={`${context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <ViviersListingSearchBar />
               </div>

               {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                    <div className={"mb-2"}>
                         <AssurancesListingPreview />
                    </div>
               )}

               <div className={`${!context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <AssurancesListingFilters />
               </div>

               <AssurancesListingList />
          </>
     )
}

// View display Xl or higher
const ViewXlOrHigher = () => {
     const context = useAssurancesListingContext()
     return (
          <>
               <div className={"row"}>
                    {/* Filters become visible when the filter icon is clicked */}
                    <div className={`col-5 ${!context.areFiltersVisible ? "d-none" : ""}`}>
                         <AssurancesListingFilters />
                    </div>
                    {/*
                     * List & preview will share the remaining width
                     * Preview will not be visible at the beginning
                     */}
                    <div className={`col ${context.itemInPreview && context.itemInPreview.isPreviewVisible ? "" : "col-7"}`}>
                         <div className={"row"}>
                              <div className={context.itemInPreview && context.itemInPreview.isPreviewVisible ? "col-5" : ""}>
                                   {/* Simple bar search will be shown only if filters are not displayed */}
                                   <div className={`mb-2 ${context.areFiltersVisible ? "d-none" : ""}`}>
                                        <ViviersListingSearchBar />
                                   </div>

                                   <AssurancesListingList />
                              </div>
                              {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                                   <div className="col-7">
                                        <AssurancesListingPreview />
                                   </div>
                              )}
                         </div>
                    </div>
               </div>
          </>
     )
}

export default AssurancesListing
